import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { engageKeys } from "../engage-keys";
import type { AttributeItem, AttributePatches } from "./attribute-values-model";

const keys = {
  conversation: (wsId: string, id: string) => [...engageKeys.inbox(wsId), "attributes", id] as const,
  lead: (wsId: string, id: string) => [...engageKeys.leads(wsId), "attributes", id] as const,
};

const asItems = (body: { items: readonly AttributeItem[] }): AttributeItem[] => [...body.items];

/** `GET /v1/conversations/{id}/attributes`: the conversation's and its visitor's defined attributes with their values. */
export function useConversationAttributes(conversationId: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: keys.conversation(wsId, conversationId),
    queryFn: async () => asItems(await unwrap(api.GET("/v1/conversations/{conversation_id}/attributes", { params: { path: { conversation_id: conversationId } } }))),
  });
}

/** `PATCH /v1/conversations/{id}/attributes`: staff set or clear values; the server announces what changed to the workflows that watch it. */
export function useSaveConversationAttributes(conversationId: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (patches: AttributePatches) =>
      asItems(await unwrap(api.PATCH("/v1/conversations/{conversation_id}/attributes", { params: { path: { conversation_id: conversationId } }, body: { conversation: patches.conversation ?? null, visitor: patches.visitor ?? null } }))),
    onSuccess: (items) => {
      client.setQueryData(keys.conversation(wsId, conversationId), items);
    },
  });
}

/** `GET /v1/leads/{id}/attributes`: the lead's defined attributes with their values. */
export function useLeadAttributes(leadId: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: keys.lead(wsId, leadId),
    queryFn: async () => asItems(await unwrap(api.GET("/v1/leads/{lead_id}/attributes", { params: { path: { lead_id: leadId } } }))),
  });
}

/** `PATCH /v1/leads/{id}/attributes`. */
export function useSaveLeadAttributes(leadId: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (patches: AttributePatches) => asItems(await unwrap(api.PATCH("/v1/leads/{lead_id}/attributes", { params: { path: { lead_id: leadId } }, body: { values: patches.lead ?? {} } }))),
    onSuccess: (items) => {
      client.setQueryData(keys.lead(wsId, leadId), items);
    },
  });
}
